import axios from 'axios';

const BASE_URL = process.env.REACT_APP_API_URI || 'http://localhost:5100/api';

class ApiClient {
  constructor() {
    this.http = axios.create({
      baseURL: BASE_URL,
      withCredentials: true,
    });

    // Auto-refresh: при 401/403 пробуем обновить токен и повторить запрос
    this.http.interceptors.response.use(
      res => res,
      async err => {
        const original = err.config;
        const status = err.response?.status;

        // Только для 401/403, не для refresh-запроса, не для повторной попытки
        if (
          (status === 401 || status === 403) &&
          !original._retry &&
          !original.url?.includes('/auth/refresh') &&
          !original.url?.includes('/auth/login') &&
          !original.url?.includes('/auth/register')
        ) {
          original._retry = true;
          try {
            const res = await this.http.post('/auth/refresh');
            const newToken = res.data?.accessToken;
            if (newToken) {
              this.setToken(newToken);
              original.headers['Authorization'] = `Bearer ${newToken}`;
              return this.http(original); // повторяем оригинальный запрос
            }
          } catch {
            // refresh тоже не сработал — очищаем токен
            this.setToken(null);
          }
        }
        return Promise.reject(err);
      }
    );
  }

  setToken(token) {
    if (token) {
      this.http.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete this.http.defaults.headers.common['Authorization'];
    }
  }

  // AUTH
  async register(data) {
    const res = await this.http.post('/auth/register', data);
    return res.data;
  }

  async login(data) {
    const res = await this.http.post('/auth/login', data);
    return res.data;
  }

  async refreshToken() {
    const res = await this.http.post('/auth/refresh');
    return res.data;
  }

  async logout() {
    const res = await this.http.post('/auth/logout');
    return res.data;
  }

  async googleAuth(credential, access_token) {
    const res = await this.http.post('/auth/google', credential ? { credential } : { access_token });
    return res.data;
  }

  async getAuthProviders() {
    const res = await this.http.get('/auth/providers');
    return res.data;
  }

  get githubAuthUrl() {
    const base = BASE_URL.replace('/api', '');
    return `${base}/api/auth/github`;
  }

  async getMe() {
    const res = await this.http.get('/auth/me');
    return res.data;
  }

  async updateMe(data) {
    const res = await this.http.patch('/auth/me', data);
    return res.data;
  }

  async changePassword(data) {
    const res = await this.http.patch('/auth/me/password', data);
    return res.data;
  }

  // ASSETS
  async listAssets(params = {}) {
    const res = await this.http.get('/assets', { params });
    return res.data;
  }

  async getAsset(id) {
    const res = await this.http.get(`/assets/${id}`);
    return res.data;
  }

  async getAssetFilters() {
    const res = await this.http.get('/assets/filters');
    return res.data;
  }

  async getMyAssets() {
    const res = await this.http.get('/assets/my');
    return res.data;
  }

  async createAsset(data) {
    const res = await this.http.post('/assets', data);
    return res.data;
  }

  async updateAsset(id, data) {
    const res = await this.http.put(`/assets/${id}`, data);
    return res.data;
  }

  async deleteAsset(id) {
    const res = await this.http.delete(`/assets/${id}`);
    return res.data;
  }

  async uploadAssetFile(assetId, formData) {
    const res = await this.http.post(`/assets/${assetId}/files`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return res.data;
  }

  async addAssetLink(assetId, data) {
    const res = await this.http.post(`/assets/${assetId}/files/link`, data);
    return res.data;
  }

  async deleteAssetFile(assetId, fileId) {
    const res = await this.http.delete(`/assets/${assetId}/files/${fileId}`);
    return res.data;
  }

  getDownloadUrl(assetId, fileId) {
    return `${BASE_URL}/assets/${assetId}/download/${fileId}`;
  }

  async submitAsset(id) {
    const res = await this.http.post(`/assets/${id}/submit`);
    return res.data;
  }

  async publishAsset(id) {
    const res = await this.http.post(`/assets/${id}/publish`);
    return res.data;
  }

  async downloadAssetFile(assetId, fileId) {
    const res = await this.http.get(`/assets/${assetId}/download/${fileId}`);
    return res.data;
  }

  async addReview(assetId, data) {
    const res = await this.http.post(`/assets/${assetId}/review`, data);
    return res.data;
  }

  async listVersions(assetId) {
    const res = await this.http.get(`/assets/${assetId}/versions`);
    return res.data;
  }

  async createVersion(assetId, changelog) {
    const res = await this.http.post(`/assets/${assetId}/versions`, { changelog });
    return res.data;
  }

  // ORDERS
  async createOrder(data) {
    const res = await this.http.post('/orders', data);
    return res.data;
  }

  async getOrders(type = 'bought') {
    const res = await this.http.get('/orders', { params: { type } });
    return res.data;
  }

  async getOrder(id) {
    const res = await this.http.get(`/orders/${id}`);
    return res.data;
  }

  // SUBSCRIPTIONS
  async createSubscription(data) {
    const res = await this.http.post('/subscriptions', data);
    return res.data;
  }

  async getSubscriptions() {
    const res = await this.http.get('/subscriptions');
    return res.data;
  }

  async cancelSubscription(id) {
    const res = await this.http.delete(`/subscriptions/${id}`);
    return res.data;
  }

  async checkSubscription(assetId) {
    const res = await this.http.get(`/subscriptions/check/${assetId}`);
    return res.data;
  }

  // DATA REQUESTS
  async listRequests(params = {}) {
    const res = await this.http.get('/requests', { params });
    return res.data;
  }

  async getMyRequests() {
    const res = await this.http.get('/requests/my');
    return res.data;
  }

  async getRequest(id) {
    const res = await this.http.get(`/requests/${id}`);
    return res.data;
  }

  async createRequest(data) {
    const res = await this.http.post('/requests', data);
    return res.data;
  }

  async makeOffer(requestId, data) {
    const res = await this.http.post(`/requests/${requestId}/offer`, data);
    return res.data;
  }

  async acceptOffer(requestId, offerId) {
    const res = await this.http.post(`/requests/${requestId}/accept/${offerId}`);
    return res.data;
  }

  async updateRequest(id, data) {
    const res = await this.http.put(`/requests/${id}`, data);
    return res.data;
  }

  async cancelRequest(id) {
    const res = await this.http.delete(`/requests/${id}`);
    return res.data;
  }

  // API KEYS
  async listApiKeys() {
    const res = await this.http.get('/keys');
    return res.data;
  }

  async createApiKey(data) {
    const res = await this.http.post('/keys', data);
    return res.data;
  }

  async revokeApiKey(id) {
    const res = await this.http.delete(`/keys/${id}`);
    return res.data;
  }

  async previewAssetData(assetId) {
    const res = await this.http.get(`/assets/${assetId}/preview`);
    return res.data;
  }

  // DATA API
  getDataUrl(assetId, format = 'json') {
    return `${BASE_URL}/assets/${assetId}/data?format=${format}`;
  }

  getSchemaUrl(assetId) {
    return `${BASE_URL}/assets/${assetId}/schema`;
  }

  // ADMIN
  async adminGetPendingAssets() {
    const res = await this.http.get('/admin/assets/pending');
    return res.data;
  }

  async adminVerifyAsset(id) {
    const res = await this.http.post(`/admin/assets/${id}/verify`);
    return res.data;
  }

  async adminRejectAsset(id, reason) {
    const res = await this.http.post(`/admin/assets/${id}/reject`, { reason });
    return res.data;
  }

  async adminGetUsers() {
    const res = await this.http.get('/admin/users');
    return res.data;
  }

  async adminToggleSeller(userId) {
    const res = await this.http.patch(`/admin/users/${userId}/seller`);
    return res.data;
  }

  async adminGetStats() {
    const res = await this.http.get('/admin/stats');
    return res.data;
  }
}

const apiClient = new ApiClient();
export default apiClient;
